/**
 * Cache for citation documents in localStorage.
 * Only use the cached document when the messageId matches the one stored
 * (so we refetch if the citation is from a different message).
 */

const KEY_PREFIX = 'citation-doc-';
const MAX_CACHE_SIZE_BYTES = 3 * 1024 * 1024; // 3MB - skip cache if larger

function cacheKey(sessionId: string, documentId: string): string {
  return `${KEY_PREFIX}${sessionId}-${documentId}`;
}

export interface CachedCitationDocument {
  messageId: string;
  base64: string;
}

/**
 * Returns a blob URL for the citation document if it exists in cache
 * and the stored messageId matches the current messageId. Otherwise returns null.
 * Caller should revoke the returned URL when done (e.g. when closing the viewer).
 */
export const getCitationDocumentFromCache = (
  sessionId: string,
  documentId: string,
  currentMessageId: string,
): string | null => {
  try {
    const key = cacheKey(sessionId, documentId);
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const parsed: CachedCitationDocument = JSON.parse(raw);
    if (parsed.messageId !== currentMessageId) return null;
    if (!parsed.base64) return null;

    const binary = atob(parsed.base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

/**
 * Stores the citation document in localStorage keyed by sessionId and documentId.
 * Stores messageId so we only use this cache for the same message.
 * Skips storing if the blob is larger than MAX_CACHE_SIZE_BYTES.
 */
export const setCitationDocumentCache = (
  sessionId: string,
  documentId: string,
  messageId: string,
  blob: Blob,
): Promise<void> => {
  if (blob.size > MAX_CACHE_SIZE_BYTES) return Promise.resolve();

  try {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 =
        typeof reader.result === 'string' ? reader.result.split(',')[1] : null;
      if (!base64) return Promise.resolve();
      const key = cacheKey(sessionId, documentId);
      const value: CachedCitationDocument = {
        messageId,
        base64,
      };
      localStorage.setItem(key, JSON.stringify(value));
      return Promise.resolve();
    };
    reader.readAsDataURL(blob);
    return Promise.resolve();
  } catch {
    return Promise.resolve();
  }
}
